import { useEffect, useState } from 'react';
import API from '../api';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const hoy = () => new Date().toISOString().slice(0, 10);
const inputStyle = { background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f' };

const emptyMov = { fecha: hoy(), tipo: 'CUOTA', valor: '', notas: '' };

function MovimientoTag({ tipo }) {
  const map = {
    CUOTA: ['Cuota', '#00b8e3', 'rgba(0,184,227,0.12)'],
    ABONO_CAPITAL: ['Abono capital', '#059669', 'rgba(5,150,105,0.10)'],
    PAGO_DEUDA: ['Pago deuda', '#6366f1', 'rgba(99,102,241,0.12)'],
  };
  const [label, color, bg] = map[tipo] || [tipo, '#5a7a8a', '#f4f7fa'];
  return <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color, background: bg, padding: '3px 8px', borderRadius: 2 }}>{label}</span>;
}

export default function CreditoAutomovil() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [mov, setMov] = useState(emptyMov);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = () => API.get('/credito-automovil').then(r => {
    setData(r.data);
    setForm({
      ...r.data.config,
      fecha_inicio: r.data.config.fecha_inicio?.slice(0, 10) || hoy(),
    });
    setMov(m => ({ ...m, valor: r.data.resumen.cuota_reajustada || r.data.resumen.cuota_inicial || '' }));
  }).catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));

  useEffect(() => { cargar(); }, []);

  const resumen = data?.resumen || {};
  const saldo = resumen.saldo_actual || 0;
  const pagado = Math.max(0, (parseFloat(form?.valor_credito) || 0) - saldo);

  async function guardarConfig(e) {
    e.preventDefault();
    setLoading(true); setMsg('');
    try {
      await API.put('/credito-automovil/config', form);
      setMsg('Parámetros actualizados');
      cargar();
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
      setTimeout(() => setMsg(''), 3200);
    }
  }

  async function guardarMovimiento(e) {
    e.preventDefault();
    setLoading(true); setMsg('');
    try {
      await API.post('/credito-automovil/movimientos', mov);
      setMov({ ...emptyMov, valor: mov.tipo === 'CUOTA' ? (resumen.cuota_reajustada || '') : '' });
      setMsg('Movimiento registrado');
      cargar();
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
      setTimeout(() => setMsg(''), 3200);
    }
  }

  async function pagarDeuda() {
    if (!saldo || !window.confirm(`¿Registrar pago total de ${fmt(saldo)}?`)) return;
    setLoading(true);
    try {
      await API.post('/credito-automovil/movimientos', { fecha: hoy(), tipo: 'PAGO_DEUDA', valor: saldo, notas: 'Pago total de deuda' });
      setMsg('Pago total registrado');
      cargar();
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
      setTimeout(() => setMsg(''), 3200);
    }
  }

  async function borrarMovimiento(id) {
    if (!window.confirm('¿Eliminar este movimiento? El crédito se recalculará.')) return;
    await API.delete(`/credito-automovil/movimientos/${id}`);
    cargar();
  }

  if (!data || !form) return <div style={{ color: '#5a7a8a', fontSize: 13 }}>Cargando crédito...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#08293f', margin: 0 }}>Crédito Automóvil</h1>
          <p style={{ fontSize: 11, color: '#5a7a8a', letterSpacing: '0.06em', marginTop: 4 }}>Control de deuda, cuotas y abonos a capital</p>
        </div>
        <button className="btn-dark" onClick={pagarDeuda} disabled={!saldo || loading}>Pagar deuda</button>
      </div>

      {msg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', fontSize: 13, fontWeight: 700,
          background: msg.startsWith('Error') ? 'rgba(248,113,113,0.1)' : 'rgba(0,184,227,0.1)',
          border: `1px solid ${msg.startsWith('Error') ? 'rgba(248,113,113,0.3)' : 'rgba(0,184,227,0.3)'}`,
          color: msg.startsWith('Error') ? '#dc2626' : '#0284c7' }}>
          {msg}
        </div>
      )}

      <div className="credito-kpi-grid" style={{ marginBottom: 12 }}>
        {[
          { label: 'Saldo actual', value: fmt(saldo), detail: `${resumen.meses_restantes} meses restantes`, dark: true },
          { label: 'Cuota reajustada', value: fmt(resumen.cuota_reajustada), detail: `cuota inicial ${fmt(resumen.cuota_inicial)}`, color: '#059669' },
          { label: 'Tasa mensual', value: `${resumen.tasa_mensual_pct}%`, detail: `${form.tasa_ea}% efectiva anual`, color: '#6366f1' },
          { label: 'Pagado a capital', value: fmt(pagado), detail: `${resumen.avance_pct}% del crédito`, color: '#08293f' },
          { label: 'Intereses pagados', value: fmt(resumen.interes_pagado), detail: 'según cuotas registradas', color: '#b45309' },
        ].map(k => (
          <div key={k.label} style={{ background: k.dark ? '#08293f' : '#fff', border: k.dark ? '1px solid rgba(0,184,227,0.25)' : '1px solid #e8eef2', borderRadius: 2, padding: '15px 16px', minHeight: 88 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: k.dark ? 'rgba(255,255,255,0.62)' : '#5a7a8a', marginBottom: 7 }}>{k.label}</div>
            <div style={{ fontSize: 21, lineHeight: 1, fontWeight: 900, color: k.dark ? '#00b8e3' : k.color, whiteSpace: 'nowrap' }}>{k.value}</div>
            <div style={{ fontSize: 11, color: k.dark ? 'rgba(255,255,255,0.65)' : '#8aa0ad', marginTop: 7 }}>{k.detail}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8eef2', height: 10, marginBottom: 16, position: 'relative' }}>
        <div style={{ height: '100%', width: `${Math.min(100, resumen.avance_pct || 0)}%`, background: '#00b8e3' }} />
      </div>

      <div className="credito-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 0.82fr) minmax(420px, 1.18fr)', gap: 12, alignItems: 'start' }}>
        <section className="card" style={{ margin: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 14 }}>— Parámetros del crédito</div>
          <form onSubmit={guardarConfig}>
            <div className="credito-form-grid">
              <div><label className="label">Nombre</label>
                <input className="input" value={form.nombre || ''} style={inputStyle} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} /></div>
              <div><label className="label">Fecha inicio</label>
                <input className="input" type="date" value={form.fecha_inicio || ''} style={inputStyle} onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} /></div>
              <div><label className="label">Valor de la deuda</label>
                <input className="input" type="number" value={form.valor_credito || ''} style={inputStyle} onChange={e => setForm(f => ({ ...f, valor_credito: e.target.value }))} required /></div>
              <div><label className="label">Tasa EA %</label>
                <input className="input" type="number" step="0.0001" value={form.tasa_ea || ''} style={inputStyle} onChange={e => setForm(f => ({ ...f, tasa_ea: e.target.value }))} required /></div>
              <div><label className="label">Plazo en meses</label>
                <input className="input" type="number" min="1" value={form.plazo_meses || ''} style={inputStyle} onChange={e => setForm(f => ({ ...f, plazo_meses: e.target.value }))} required /></div>
            </div>
            <div style={{ marginTop: 12 }}><label className="label">Notas</label>
              <textarea className="input" rows={3} value={form.notas || ''} style={{ ...inputStyle, resize: 'vertical' }} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} /></div>
            <button className="btn-primary" disabled={loading} style={{ width: '100%', marginTop: 14 }}>{loading ? 'Guardando...' : 'Actualizar crédito'}</button>
          </form>
        </section>

        <section className="card" style={{ margin: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Registrar movimiento</div>
              <div style={{ fontSize: 12, color: '#8aa0ad', marginTop: 3 }}>La cuota divide interés y capital; el abono capital reduce saldo directo.</div>
            </div>
          </div>
          <form onSubmit={guardarMovimiento} className="credito-mov-grid">
            <div><label className="label">Fecha</label>
              <input className="input" type="date" value={mov.fecha} style={inputStyle} onChange={e => setMov(m => ({ ...m, fecha: e.target.value }))} required /></div>
            <div><label className="label">Tipo</label>
              <select className="input" value={mov.tipo} style={inputStyle} onChange={e => setMov(m => ({ ...m, tipo: e.target.value, valor: e.target.value === 'CUOTA' ? resumen.cuota_reajustada : '' }))}>
                <option value="CUOTA">Cuota normal</option>
                <option value="ABONO_CAPITAL">Abono a capital</option>
                <option value="PAGO_DEUDA">Pagar deuda</option>
              </select></div>
            <div><label className="label">Valor</label>
              <input className="input" type="number" value={mov.tipo === 'PAGO_DEUDA' && !mov.valor ? saldo : mov.valor} style={inputStyle}
                onChange={e => setMov(m => ({ ...m, valor: e.target.value }))} required /></div>
            <div style={{ gridColumn: '1 / -1' }}><label className="label">Notas</label>
              <input className="input" value={mov.notas} style={inputStyle} onChange={e => setMov(m => ({ ...m, notas: e.target.value }))} placeholder="Ej: abono extraordinario, cuota banco..." /></div>
            <button className="btn-primary" disabled={loading} style={{ gridColumn: '1 / -1' }}>{loading ? 'Registrando...' : 'Guardar movimiento'}</button>
          </form>
        </section>
      </div>

      <div className="credito-bottom-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(520px, 1.15fr) minmax(320px, 0.85fr)', gap: 12, marginTop: 12, alignItems: 'start' }}>
        <section className="card" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid #e8eef2', background: '#f9fafb' }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Cuotas y abonos registrados</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" style={{ margin: 0 }}>
              <thead><tr>
                <th>Fecha</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Valor</th><th style={{ textAlign: 'right' }}>Interés</th><th style={{ textAlign: 'right' }}>Capital</th><th style={{ textAlign: 'right' }}>Saldo</th><th></th>
              </tr></thead>
              <tbody>
                {data.movimientos.map(m => (
                  <tr key={m.id}>
                    <td style={{ color: '#5a7a8a', fontSize: 12, whiteSpace: 'nowrap' }}>{m.fecha?.slice(0, 10)}</td>
                    <td><MovimientoTag tipo={m.tipo} />{m.notas && <div style={{ fontSize: 10, color: '#b3b3b3', marginTop: 2 }}>{m.notas}</div>}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#08293f' }}>{fmt(m.valor)}</td>
                    <td style={{ textAlign: 'right', color: '#b45309' }}>{fmt(m.interes)}</td>
                    <td style={{ textAlign: 'right', color: '#059669', fontWeight: 800 }}>{fmt(m.capital)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmt(m.saldo_despues)}</td>
                    <td><button onClick={() => borrarMovimiento(m.id)} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Borrar</button></td>
                  </tr>
                ))}
                {data.movimientos.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 34, color: '#b3b3b3' }}>Sin movimientos registrados</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid #e8eef2', background: '#f9fafb' }}>
            <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Próximas cuotas estimadas</div>
          </div>
          {data.proyeccion.map(p => (
            <div key={p.mes} style={{ padding: '12px 16px', borderBottom: '1px solid #edf3f7' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <b style={{ color: '#08293f' }}>Mes {p.mes}</b>
                <span style={{ fontWeight: 900, color: '#08293f' }}>{fmt(p.cuota)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8, fontSize: 11 }}>
                <span style={{ color: '#5a7a8a' }}>{p.fecha}</span>
                <span style={{ color: '#b45309' }}>Interés {fmt(p.interes)}</span>
                <span style={{ color: '#059669', textAlign: 'right' }}>Saldo {fmt(p.saldo)}</span>
              </div>
            </div>
          ))}
          {data.proyeccion.length === 0 && <div style={{ padding: 34, textAlign: 'center', color: '#b3b3b3', fontSize: 13 }}>Crédito sin saldo pendiente</div>}
        </section>
      </div>

      <style>{`
        .credito-kpi-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; }
        .credito-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .credito-mov-grid { display: grid; grid-template-columns: 150px 1fr 1fr; gap: 12px; align-items: end; }
        @media (max-width: 900px) {
          .credito-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .credito-layout, .credito-bottom-grid { grid-template-columns: 1fr !important; }
          .credito-mov-grid { grid-template-columns: 1fr; }
          .credito-form-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
